import React, { useState, useEffect, useMemo } from 'react';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { Search1Stroke } from '@lineiconshq/free-icons';
import { Bell, Settings, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { MenuItem, NavMenu } from '@/constants/navigation/types';
import { useDebounce } from '@/hooks/use-debounce';
import { useAuth } from '@/contexts/auth-context';
import { menus } from '@/constants/navigation';
import { AppLink } from '@/routing';

interface SearchItem {
  title: string;
  category: string;
  href: string;
  path: string[];
}

const getSearchItems = (menus: NavMenu[]): SearchItem[] => {
  const items: SearchItem[] = [];

  const traverse = (menuItem: MenuItem, currentPath: string[], category: string) => {
    const newPath = [...currentPath, menuItem.title];
    if (menuItem.href) {
      items.push({
        title: menuItem.title,
        category,
        href: menuItem.href,
        path: newPath
      });
    }
    if (menuItem.items) {
      menuItem.items.forEach(child => traverse(child, newPath, category));
    }
  };

  menus.forEach(menu => {
    if (menu.items) {
      menu.items.forEach(item => traverse(item, [menu.title], menu.title));
    }
  });

  return items;
};

const removeAccents = (str: string): string => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
};

const getMatchScore = (item: SearchItem, query: string): number => {
  if (!query) return 0;
  const q = removeAccents(query.trim().toLowerCase());
  const title = removeAccents(item.title.toLowerCase());

  if (title === q) return 10000;
  if (title.startsWith(q)) return 8000;
  if (title.includes(q)) return 6000;

  const qWords = q.split(/\s+/).filter(Boolean);
  let matchCount = 0;
  qWords.forEach(word => {
    if (title.includes(word)) {
      matchCount += 10;
    }
  });

  const pathStr = removeAccents(item.path.join(' ').toLowerCase());
  qWords.forEach(word => {
    if (pathStr.includes(word)) {
      matchCount += 1;
    }
  });

  return matchCount;
};

export function Logo({ isMobile = false }: { isMobile?: boolean }) {
  return (
    <AppLink
      href='/trang-chu'
      className={`flex cursor-pointer items-center ${isMobile ? 'px-3 py-2' : 'py-1.5 pl-1.5 pr-3'}`}
    >
      <div className='flex select-none items-baseline'>
        <span
          className={isMobile ? 'text-lg' : 'text-xl'}
          style={{
            fontFamily: "'Be Vietnam Pro', sans-serif",
            fontWeight: 900,
            letterSpacing: '0.02em',
            color: '#FFFFFF'
          }}
        >
          TTMI
        </span>
        <span
          className={isMobile ? 'text-lg' : 'text-xl'}
          style={{
            fontFamily: "'Be Vietnam Pro', sans-serif",
            fontWeight: 900,
            letterSpacing: '0.02em',
            color: '#4CA3A5',
            marginLeft: '6px'
          }}
        >
          ERP
        </span>
      </div>
    </AppLink>
  );
}

export function ProfileButton({
  isMobile = false,
  onClick
}: {
  isMobile?: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  const { profile } = useAuth();
  const nickname = profile?.khai_bao_nguoi_su_dung_data?.nickname || profile?.nickname || '';
  const firstLetter = nickname.trim().charAt(0).toUpperCase() || 'A';

  return (
    <div className='flex items-center gap-2'>
      {!isMobile && nickname && <span className='text-xs font-medium text-main-foreground/90'>{nickname}</span>}
      <button
        onClick={onClick}
        className={`flex cursor-pointer items-center justify-center rounded-full bg-main-foreground text-xs font-bold text-main shadow-sm transition-all duration-200 hover:scale-105 hover:shadow focus:outline-none ${
          isMobile ? 'h-7 w-7' : 'h-[var(--erp-header-icon-size)] w-[var(--erp-header-icon-size)]'
        }`}
      >
        {firstLetter}
      </button>
    </div>
  );
}

// Company selector with user and entity info styled as a clean glass-pill
export function CompanySelector({ isMobile = false }: { isMobile?: boolean }) {
  const { profile, entity } = useAuth();

  return (
    <div
      className={`flex cursor-pointer items-center gap-2 rounded-full border border-main-foreground/15 bg-main-foreground/10 transition-all duration-200 hover:bg-main-foreground/15 ${
        isMobile ? 'px-2 py-0.5' : 'px-3 py-1'
      }`}
    >
      <span className='text-xs font-semibold text-main-foreground'>{entity?.name || 'N/A'}</span>
    </div>
  );
}

// Header icons (search and notifications) using Lucide and round hover states
export function HeaderIcons({ isMobile = false }: { isMobile?: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && e.ctrlKey) {
        e.preventDefault();
        setOpen(open => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const groupedItems = useMemo(() => {
    const searchItems = getSearchItems(menus);
    const groups: { [category: string]: SearchItem[] } = {};
    searchItems.forEach(item => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, []);

  const orderedGroups = useMemo(() => {
    const categories = Object.keys(groupedItems);
    if (!debouncedSearch.trim()) {
      return categories.map(cat => ({
        category: cat,
        items: groupedItems[cat]
      }));
    }

    const scoredCategories = categories.map(cat => {
      const itemsWithScore = groupedItems[cat].map(item => ({
        item,
        score: getMatchScore(item, debouncedSearch)
      }));

      itemsWithScore.sort((a, b) => b.score - a.score);

      const maxScore = itemsWithScore.length > 0 ? itemsWithScore[0].score : 0;

      return {
        category: cat,
        items: itemsWithScore.map(x => x.item),
        maxScore
      };
    });

    scoredCategories.sort((a, b) => b.maxScore - a.maxScore);

    return scoredCategories;
  }, [groupedItems, debouncedSearch]);

  const handleSelect = (href: string) => {
    setOpen(false);
    setSearch('');
    navigate(href);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`flex items-center justify-center rounded-full text-main-foreground/80 transition-all duration-200 hover:bg-main-foreground/10 hover:text-main-foreground focus:outline-none ${
          isMobile ? 'h-7 w-7' : 'h-[var(--erp-header-icon-size)] w-[var(--erp-header-icon-size)]'
        }`}
        title='Tìm kiếm chức năng (Ctrl+K)'
      >
        <Search className={isMobile ? 'h-4 w-4' : 'h-5 w-5'} />
      </button>

      <button
        className={`relative flex items-center justify-center rounded-full text-main-foreground/80 transition-all duration-200 hover:bg-main-foreground/10 hover:text-main-foreground focus:outline-none ${
          isMobile ? 'h-7 w-7' : 'h-[var(--erp-header-icon-size)] w-[var(--erp-header-icon-size)]'
        }`}
      >
        <Bell className={isMobile ? 'h-4 w-4' : 'h-5 w-5'} />
      </button>

      <button
        className={`relative flex items-center justify-center rounded-full text-main-foreground/80 transition-all duration-200 hover:bg-main-foreground/10 hover:text-main-foreground focus:outline-none ${
          isMobile ? 'h-7 w-7' : 'h-[var(--erp-header-icon-size)] w-[var(--erp-header-icon-size)]'
        }`}
        title='Cấu hình hệ thống'
      >
        <Settings className={`animate-[spin_15s_linear_infinite] ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} className='h-[80vh]'>
        <CommandInput placeholder='Tìm kiếm chức năng (Ctrl+K)' value={search} onValueChange={setSearch} />
        <CommandList className='max-h-none flex-1'>
          <CommandEmpty>Không tìm thấy kết quả.</CommandEmpty>
          {orderedGroups.map(({ category, items }) => (
            <CommandGroup key={category} heading={category}>
              {items.map(item => (
                <CommandItem
                  key={item.href + item.title}
                  value={`${item.title} ${item.path.join(' ')}`}
                  onSelect={() => handleSelect(item.href)}
                  className='flex cursor-pointer items-center justify-between rounded-md px-3 py-2 transition-all duration-150'
                >
                  <div className='flex w-full items-center gap-3'>
                    <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-main/10 text-main transition-colors'>
                      <Lineicons aria-hidden='true' icon={Search1Stroke} className='h-4 w-4 opacity-50' />
                    </div>
                    <div className='flex min-w-0 flex-1 flex-col'>
                      <span className='text-sm font-medium leading-tight text-foreground/95'>{item.title}</span>
                      <span className='mt-0.5 truncate text-[11px] font-normal text-muted-foreground/80'>
                        {item.path.join('  ›  ')}
                      </span>
                    </div>
                  </div>
                  <div className='hidden items-center rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:flex'>
                    Enter
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
